/**
 * BasicInfoDrawer — Edit device info form (description, serial, behavior/profile, assignment).
 *
 * Uses the dp-drawer always-render pattern for smooth slide-in animation.
 * Conditionally renders:
 *   - Inovonics: Behavior dropdown (from useMapData)
 *   - Palcare:   Line Profile dropdown (from dspProfiles)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';
import { areaOptions, residentOptions, normalizeSerial, validateSerial } from '../../components/advLearnIn.js';
import Drawer from '../../../../components/v2/Drawer.jsx';
import DeviceAlarmWarning from '../../../../components/v2/DeviceAlarmWarning.jsx';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  description: initDesc, serialNum: initSerial, deviceUseId: initUseId,
  isAreaDevice, areaId: initAreaId, residentId: initResidentId,
  areas, residents, useMapData,
  /* Palcare-specific props */
  isPalcare, dspProfileId: initDspProfileId, dspProfiles,
  /* For normalizeSerial: which manufacturer / device type rules the serial follows. */
  manufactureId, deviceTypeId,
  /* The device is in alarm: saving is allowed, with DeviceAlarmWarning. */
  alertActive = false,
}) => {
  const [description, setDescription] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [deviceUseId, setDeviceUseId] = useState(0);
  const [dspProfileId, setDspProfileId] = useState(0);
  const [linkedId, setLinkedId] = useState(0);
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setDescription(initDesc || '');
      setSerialNum(initSerial || '');
      setDeviceUseId(initUseId || 0);
      setDspProfileId(initDspProfileId || 0);
      setLinkedId(isAreaDevice ? (initAreaId || 0) : (initResidentId || 0));
      setErrors({});
    }
    prevOpen.current = open;
  });

  /* The serial as it is stored: trimmed, and with an all-numeric serial's leading zeros
     dropped, the same normalizeSerial the add drawers use. "000123" and "123" must not
     become two serials for one device, nor fail to match the device on the next learn-in. */
  const serialEntry = useMemo(
    () => ({ manufacture_id: Number(manufactureId), device_type_id: Number(deviceTypeId) }),
    [manufactureId, deviceTypeId],
  );
  /* The add drawers' rules, so a serial that could be added can be saved here and one
     that couldn't be added can't be saved either: validateSerial (Inovonics numbers only,
     at least 4 digits, in range; everyone else letters and numbers; a PalCare pendant
     starts with "PC"). This used to demand numbers only for every device, so no PalCare
     pendant could be saved. */
  const validate = useCallback((raw) => {
    const errs = {};
    const serial = normalizeSerial(raw, serialEntry);
    /* An unchanged serial is not re-judged: a device saved before these rules (a
       3-digit Inovonics serial, say) must still be able to change its location or
       assignment. It only has to be present. */
    const unchanged = serial !== '' && serial === normalizeSerial(initSerial || '', serialEntry);
    if (!unchanged) {
      const msg = validateSerial(raw, serialEntry);
      if (msg) errs.serialNum = msg;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [serialEntry, initSerial]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate(serialNum)) return;
    const serial = normalizeSerial(serialNum, serialEntry);
    // Show what is being saved, so "000123" visibly becomes "123".
    setSerialNum(serial);
    onSave({
      description: description.trim(),
      serialNum: serial,
      deviceUseId,
      dspProfileId,
      linkedId,
    });
  }, [validate, serialNum, serialEntry, onSave, description, deviceUseId, dspProfileId, linkedId]);

  return (
    /* No inline z-index. `.dp-drawer` gets --z-modal (500) from the stylesheet, and
       SearchableSelect portals its menu to document.body at 600 — so the drawer must
       stay BELOW that. A hardcoded 1001 here (left over from when this drawer used
       the V1 SearchableDropdown, whose menu rendered inline and so was unaffected)
       painted the drawer over the portalled menu: the options were in the DOM, in the
       right place, and completely invisible. Every other drawer in the app relies on
       the token, which is why the identical picker in the Add Resident drawer works. */
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit Device Info"
      backdrop={false}
      bodyAs="form"
      bodyProps={{ onSubmit: handleSubmit }}
      footer={(
        <>
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    >
      {/* Saving while the device is in alarm is allowed; say what stays behind. */}
      <DeviceAlarmWarning show={alertActive} />

      {/* Location within area — area devices only, matching the condition the
          Basic Info card uses to show it. A resident device is identified by
          the person it belongs to, so placement text has nothing to describe.
          The value still round-trips on save for non-area devices because it's
          seeded from props and can't be edited while hidden. */}
      {isAreaDevice ? (
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Location within area
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional... Bedroom, Bathroom, etc."
            autoFocus
          />
          {errors.description && <p className="dp-drawer__error">{errors.description}</p>}
        </div>
      ) : null}

      {/* Serial Number */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          Serial Number <span className="dp-drawer__required">*</span>
        </label>
        <input
          type="text"
          className={`dp-drawer__input${errors.serialNum ? ' is-invalid' : ''}`}
          value={serialNum}
          autoFocus={!isAreaDevice}
          onChange={(e) => setSerialNum(e.target.value)}
          placeholder={isPalcare ? 'Serial number' : 'Numeric serial number'}
        />
        {errors.serialNum && <p className="dp-drawer__error">{errors.serialNum}</p>}
      </div>

      {/* Behavior (Inovonics) */}
      {!isPalcare && useMapData.length > 0 && (
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Behavior</label>
          <select
            className="dp-drawer__select"
            value={deviceUseId}
            onChange={(e) => setDeviceUseId(e.target.value)}
          >
            {useMapData.map((item, i) => (
              <option key={i} value={item.inovonics_device_use_id}>
                {item.option_text || 'Standard'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Line Profile (Palcare) */}
      {isPalcare && dspProfiles?.length > 0 && (
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Line Profile</label>
          <select
            className="dp-drawer__select"
            value={dspProfileId}
            onChange={(e) => setDspProfileId(e.target.value)}
          >
            {dspProfiles.map((item, i) => (
              <option key={i} value={item.dsp_profile_id}>
                {item.profile_name || 'No Profile'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Assignment — V2 SearchableSelect, matching the Add Resident drawer's
          area picker. Replaced the V1 SearchableDropdown (bootstrap markup with
          its own floating label), which looked nothing like the rest of the V2
          forms around it.

          Option shapes come from advLearnIn.js so the sorting and the search
          behaviour are identical to the advanced learn-in drawer: areas by name,
          residents by first name, and searching matches the raw fields rather
          than the composed label. */}
      <div className="dp-drawer__field">
        <label htmlFor="edit-linked" className="dp-drawer__label">
          {isAreaDevice ? 'Assigned Area' : 'Assigned Resident'}{' '}
          <span className="dp-drawer__required">*</span>
        </label>
        <SearchableSelect
          id="edit-linked"
          options={isAreaDevice ? areaOptions(areas) : residentOptions(residents)}
          value={linkedId}
          /* State stays numeric with 0 meaning unassigned — that is what onSave
             forwards to the API. SearchableSelect hands back the option's string
             value or null, so both directions are converted here rather than
             changing the payload's shape. */
          onChange={(val) => setLinkedId(val ? Number(val) : 0)}
          placeholder={isAreaDevice ? 'Search or select area...' : 'Search or select resident...'}
          ariaLabel={isAreaDevice ? 'Assigned Area' : 'Assigned Resident'}
          required
          isClearable
        />
      </div>

      {/* Footer */}
    </Drawer>
  );
};

export default BasicInfoDrawer;
